import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { InformationCircleIcon, ArrowPathIcon, BugAntIcon, LightBulbIcon, ArrowTrendingUpIcon } from '@heroicons/react/24/outline';
import ScrollableDropdown from '../components/ScrollableDropdown';

const STATUS_COLUMNS = [
  { key: 'Open', title: 'Open' },
  { key: 'In Progress', title: 'In Progress (Bug Fixing)' },
  { key: 'On Hold', title: 'On Hold' },
  { key: 'Fixed', title: 'Fixed' },
  { key: 'Retesting In Progress', title: 'Retesting (In Progress)' },
  { key: 'Closed', title: 'Closed / Retested' },
  { key: 'Recurring Issue', title: 'Recurring Issue' }
];

const headerColors = {
  'Open': 'bg-blue-50 text-blue-800 border-blue-100',
  'In Progress': 'bg-orange-50 text-orange-800 border-orange-100',
  'On Hold': 'bg-amber-50 text-amber-800 border-amber-100',
  'Fixed': 'bg-green-50 text-green-800 border-green-100',
  'Retesting In Progress': 'bg-yellow-50 text-yellow-800 border-yellow-100',
  'Closed': 'bg-emerald-50 text-emerald-800 border-emerald-100',
  'Recurring Issue': 'bg-red-50 text-red-800 border-red-100'
};

const TICKET_TYPE_CONFIG = {
  'Bug':         { icon: BugAntIcon,           color: 'text-rose-600',  badge: 'bg-red-50 text-red-700 border-red-100' },
  'Observation': { icon: LightBulbIcon,        iconColor: 'text-amber-500', badge: 'bg-amber-50 text-amber-700 border-amber-100' },
  'Improvement': { icon: ArrowTrendingUpIcon,  iconColor: 'text-green-500', badge: 'bg-green-50 text-green-700 border-green-100' }
};



const KanbanView = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const initialSelected = useMemo(() => {
    const fromState = location.state?.selectedApplication || null;
    const fromStorage = localStorage.getItem('selectedApp') || null;
    return fromState || fromStorage || '';
  }, [location.state]);

  const [selectedApp, setSelectedApp] = useState(initialSelected);
  const [applications, setApplications] = useState([]);
  const [bugs, setBugs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSelectAppInfo, setShowSelectAppInfo] = useState(false);
  const [updatingBugId, setUpdatingBugId] = useState(null);

  const dragDataRef = useRef({ bugId: null, fromStatus: null });

  // Close Bug Modal state (for Kanban drag-to-Closed)
  const [showCloseBugModalKanban, setShowCloseBugModalKanban] = useState(false);
  const [closeBugVerificationLinkKanban, setCloseBugVerificationLinkKanban] = useState('');
  const [closeBugVerificationNotesKanban, setCloseBugVerificationNotesKanban] = useState('');
  const [pendingCloseBugKanban, setPendingCloseBugKanban] = useState(null);
  const [pendingVerificationStatusKanban, setPendingVerificationStatusKanban] = useState('Closed');

  const fetchAllBugsForApp = useCallback(async (appId) => {
    if (!appId) return;
    const cachedKey = `kanban:bugs:${appId}`;
    try {
      const cached = sessionStorage.getItem(cachedKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed)) {
          setBugs(parsed);
        }
      }
    } catch { }
    setLoading(true);
    try {
      const first = await api.get(`/api/bugs?page=1&pageSize=100&application=${appId}`);
      const totalPages = first.data.totalPages || 1;
      const data = [...(first.data.data || [])];
      for (let p = 2; p <= totalPages; p++) {
        const res = await api.get(`/api/bugs?page=${p}&pageSize=100&application=${appId}`);
        data.push(...(res.data.data || []));
      }
      setBugs(data);
      try {
        sessionStorage.setItem(cachedKey, JSON.stringify(data));
      } catch { }
    } catch (err) {
      console.error('Failed to fetch bugs:', err);
      toast.error('Failed to load Kanban data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchApplications = useCallback(async () => {
    try {
      const { data } = await api.get('/api/applications');
      const active = data.data
        .filter(app => ['Active', 'Testing'].includes(app.status))
        .sort((a, b) => String(a.name).localeCompare(String(b.name)));
      setApplications(active);
      const currentSelected = localStorage.getItem('selectedApp');
      if (!currentSelected || !active.some(a => a._id === currentSelected)) {
        const first = active[0]?._id || '';
        if (first) {
          setSelectedApp(first);
          localStorage.setItem('selectedApp', first);
          await fetchAllBugsForApp(first);
        } else {
          setShowSelectAppInfo(true);
        }
      }
    } catch (err) {
      console.error('Failed to fetch applications:', err);
    }
  }, [fetchAllBugsForApp]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  useEffect(() => {
    if (!selectedApp) {
      setShowSelectAppInfo(true);
      setBugs([]);
      return;
    }
    setShowSelectAppInfo(false);
    localStorage.setItem('selectedApp', selectedApp);
    fetchAllBugsForApp(selectedApp);
  }, [selectedApp, fetchAllBugsForApp]);

  useEffect(() => {
    const onBugUpdated = (evt) => {
      const appId = evt?.detail?.applicationId;
      if (!appId || !selectedApp || String(appId) !== String(selectedApp)) return;
      fetchAllBugsForApp(selectedApp);
    };
    window.addEventListener('bugtracker:bug-updated', onBugUpdated);
    return () => window.removeEventListener('bugtracker:bug-updated', onBugUpdated);
  }, [selectedApp, fetchAllBugsForApp]);

  const grouped = useMemo(() => {
    const map = new Map();
    STATUS_COLUMNS.forEach(c => map.set(c.key, []));
    bugs.forEach(b => {
      const status = b.status || 'Open';
      if (!map.has(status)) {
        if (STATUS_COLUMNS.some(c => c.key === status)) {
          map.set(status, []);
        } else {
          map.set(status, []);
        }
      }
      map.get(status).push(b);
    });
    return map;
  }, [bugs]);

  const canDropToStatus = (toStatus) => {
    if (user.role === 'manager') return false;
    if (user.role === 'developer') {
      return ['In Progress', 'Fixed', 'On Hold'].includes(toStatus);
    }
    if (user.role === 'tester') {
      return ['Retesting In Progress', 'Closed', 'Reopen', 'Recurring Issue', 'On Hold'].includes(toStatus);
    }
    return false;
  };

  const handleStatusUpdate = async (bugId, newStatus, proofData = null) => {
    setUpdatingBugId(bugId);
    try {
      const payload = { status: newStatus };
      if (proofData) {
        if (proofData.proofUrl) {
          payload.proofUrl = proofData.proofUrl;
        }
        if (proofData.description) {
          payload.proofDescription = proofData.description;
        }
      }
      const { data } = await api.put(`/api/bugs/${bugId}`, payload);
      setBugs(prev => prev.map(b => (b._id === bugId ? data.data : b)));
      toast.success('Status updated');

      // Dispatch event for other components to refresh (e.g., Dashboard)
      window.dispatchEvent(
        new CustomEvent('bugtracker:bug-updated', {
          detail: {
            bugId: bugId,
            status: newStatus,
            applicationId: data?.data?.application?._id || data?.data?.application || selectedApp
          }
        })
      );
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Update failed';
      toast.error(msg);
    } finally {
      setUpdatingBugId(null);
    }
  };

  const onDragStart = (bug) => {
    dragDataRef.current = { bugId: bug._id, fromStatus: bug.status || 'Open' };
  };

  const onDragOver = (e, status) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const onDrop = async (e, toStatus) => {
    e.preventDefault();
    const { bugId } = dragDataRef.current;
    if (!bugId) return;
    if (!canDropToStatus(toStatus)) {
      toast.error('Not allowed to move to this column');
      return;
    }
    const bug = bugs.find(b => b._id === bugId);
    if (!bug) return;
    // Frontend guard: enforce workflow preconditions to avoid 400s
    if (user.role === 'tester') {
      if (toStatus === 'Retesting In Progress' && !['Fixed', 'Retesting In Progress'].includes(bug.status)) {
        toast.error('Retesting is available after the bug is marked Fixed');
        return;
      }
      if (toStatus === 'Closed' && !['Fixed', 'Retesting In Progress'].includes(bug.status)) {
        toast.error('Close is available after the bug is marked Fixed');
        return;
      }
    }
    if (user.role === 'tester' && (toStatus === 'Closed' || toStatus === 'Reopen')) {
      // Show the verification modal instead of updating directly
      setPendingCloseBugKanban({ bugId, bug });
      setPendingVerificationStatusKanban(toStatus);
      setCloseBugVerificationLinkKanban('');
      setCloseBugVerificationNotesKanban('');
      setShowCloseBugModalKanban(true);
      return;
    }
    await handleStatusUpdate(bugId, toStatus);
  };

  const handleCloseBugSubmitKanban = async () => {
    if (!pendingCloseBugKanban) return;
    const { bugId } = pendingCloseBugKanban;
    setShowCloseBugModalKanban(false);
    setPendingCloseBugKanban(null);
    const proofData = {
      proofUrl: closeBugVerificationLinkKanban.trim(),
      description: closeBugVerificationNotesKanban.trim()
    };
    await handleStatusUpdate(bugId, pendingVerificationStatusKanban, proofData);
  };

  const columnCount = (statusKey) => (grouped.get(statusKey) || []).length;

  const Card = ({ bug }) => {
    const idLabel = `#${bug.bugId || (bug._id || '').slice(-6).toUpperCase()}`;
    const disabled = updatingBugId === bug._id;
    const type = bug.ticketType || 'Bug';
    const config = TICKET_TYPE_CONFIG[type] || TICKET_TYPE_CONFIG['Bug'];
    const Icon = config.icon;

    return (
      <div
        draggable={!disabled && user.role !== 'manager'}
        onDragStart={() => onDragStart(bug)}
        onClick={() => navigate(`/bugs/${bug.bugId || bug._id}`)}
        className={`bg-white border border-gray-200 rounded-md p-2.5 shadow-sm cursor-pointer transition-all ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md hover:border-gray-300'}`}
      >
        <div className="flex items-center justify-between mb-1.5">
          <span className={`inline-flex items-center gap-1 font-mono text-[10px] font-bold px-1.5 py-0.5 rounded border ${config.badge}`}>
            <Icon className={`h-3 w-3 ${config.iconColor || 'text-rose-600'}`} />
            {idLabel}
          </span>
          <span className="text-[10px] text-gray-500 truncate max-w-[50%]">
            {bug.assignedTo?.name ? bug.assignedTo.name : (bug.status || 'Open')}
          </span>
        </div>
        <div className="text-[12px] font-semibold text-gray-800 leading-snug line-clamp-2" title={bug.title}>
          {bug.title}
        </div>
      </div>
    );
  };

  return (
    <div className="w-full flex justify-center">
      <div className="w-[90vw] max-w-[1400px] px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <h1 className="text-lg sm:text-xl font-bold text-gray-900">Kanban View</h1>
            {selectedApp && (
              <button
                onClick={() => fetchAllBugsForApp(selectedApp)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200"
              >
                <ArrowPathIcon className="h-4 w-4" />
                Refresh
              </button>
            )}
          </div>
          <div className="w-64">
            <ScrollableDropdown
              value={selectedApp || ''}
              onChange={(val) => setSelectedApp(val)}
              options={(applications || []).slice().sort((a, b) => String(a.name).localeCompare(String(b.name))).map(app => ({ value: app._id, label: app.name }))}
              placeholder="Select Application"
              buttonHeightClassName="h-10"
              maxMenuHeightPx={320}
              maxMenuHeightClassName="max-h-80"
              usePortal={true}
              disableHorizontalScroll={true}
              menuClassName="z-50"
            />
          </div>
        </div>

        {showSelectAppInfo && (
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-xl mb-4">
            <div className="flex">
              <InformationCircleIcon className="h-5 w-5 text-blue-500 mr-3 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-700">
                <p className="font-semibold">Please select an application first, then open Kanban View.</p>
              </div>
            </div>
          </div>
        )}

        {!selectedApp ? (
          <div className="text-sm text-gray-500">No application selected.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-2">
            {STATUS_COLUMNS.map(col => (
              <div key={col.key} className="bg-gray-50 rounded-lg border border-gray-200 flex flex-col min-h-[280px]">
                <div className={`px-3 py-2 border-b rounded-t-lg ${headerColors[col.key] || 'bg-gray-50 text-gray-800 border-gray-200'}`}>
                  <div className="flex items-center justify-between">
                    <h3 className="text-[11px] font-extrabold uppercase tracking-wider">{col.title}</h3>
                    <span className="text-[10px] font-bold bg-white/70 border border-gray-200 rounded px-2 py-0.5">
                      {columnCount(col.key)}
                    </span>
                  </div>
                </div>
                <div
                  className="flex-1 p-2 space-y-2 overflow-y-auto"
                  onDragOver={(e) => onDragOver(e, col.key)}
                  onDrop={(e) => onDrop(e, col.key)}
                >
                  {loading ? (
                    <>
                      <div className="animate-pulse bg-white border border-gray-200 rounded-md h-14" />
                      <div className="animate-pulse bg-white border border-gray-200 rounded-md h-14" />
                      <div className="animate-pulse bg-white border border-gray-200 rounded-md h-14" />
                    </>
                  ) : (
                    (grouped.get(col.key) || []).map(bug => (
                      <Card key={bug._id} bug={bug} />
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Close Bug Modal — shown when tester drags a bug to the 'Closed' column */}
        {showCloseBugModalKanban && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center" role="dialog" aria-modal="true" aria-labelledby="close-bug-modal-kanban-title">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={() => { setShowCloseBugModalKanban(false); setPendingCloseBugKanban(null); }}
            />
            {/* Modal Card */}
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
              {/* Header */}
              <div className="px-6 pt-6 pb-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h2 id="close-bug-modal-kanban-title" className="text-base font-bold text-gray-900">{pendingVerificationStatusKanban === 'Reopen' ? `Reopen ${pendingCloseBugKanban?.bug?.ticketType || 'Bug'}` : `Close ${pendingCloseBugKanban?.bug?.ticketType || 'Bug'}`}</h2>
                    <p className="text-xs text-gray-500 mt-0.5">{pendingVerificationStatusKanban === 'Reopen' ? `Provide details for reopening ${(pendingCloseBugKanban?.bug?.ticketType || 'Bug').toLowerCase()} (optional)` : `Provide verification details for ${(pendingCloseBugKanban?.bug?.ticketType || 'Bug').toLowerCase()} (optional)`}</p>
                  </div>
                </div>
              </div>
              {/* Body */}
              <div className="px-6 py-5 space-y-4">
                <div>
                  <label htmlFor="close-bug-kanban-link" className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">
                    Verification Link
                  </label>
                  <input
                    id="close-bug-kanban-link"
                    type="url"
                    value={closeBugVerificationLinkKanban}
                    onChange={(e) => setCloseBugVerificationLinkKanban(e.target.value)}
                    placeholder="Enter link to screenshot, video, or test evidence (optional)"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all placeholder:text-gray-400 bg-gray-50 focus:bg-white"
                  />
                </div>
                <div>
                  <label htmlFor="close-bug-kanban-notes" className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">
                    Verification Notes
                  </label>
                  <textarea
                    id="close-bug-kanban-notes"
                    rows={3}
                    value={closeBugVerificationNotesKanban}
                    onChange={(e) => setCloseBugVerificationNotesKanban(e.target.value)}
                    placeholder={pendingVerificationStatusKanban === 'Reopen' ? '👉 Describe why the issue still persists' : `Describe how the ${(pendingCloseBugKanban?.bug?.ticketType || 'Bug').toLowerCase()} fix was verified (optional)`}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all placeholder:text-gray-400 bg-gray-50 focus:bg-white resize-none"
                  />
                </div>
              </div>
              {/* Footer */}
              <div className="px-6 pb-6 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => { setShowCloseBugModalKanban(false); setPendingCloseBugKanban(null); }}
                  className="px-4 py-2 text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCloseBugSubmitKanban}
                  className="px-5 py-2 text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-md transition-colors shadow-sm"
                >
                  {pendingVerificationStatusKanban === 'Reopen' ? `Reopen ${pendingCloseBugKanban?.bug?.ticketType || 'Bug'}` : `Close ${pendingCloseBugKanban?.bug?.ticketType || 'Bug'}`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KanbanView;
